import { Injectable, signal } from "@angular/core";
import { ACTION, Permission, RESOURCE } from "@models/role";
import { BehaviorSubject } from "rxjs";
import { LocalStorageService } from "./local-storage.service";

@Injectable({
    providedIn: 'root'
})
export class PermissionService {

    private _permissions = signal<Permission[]>([]);
    private _features = signal<string[]>([]);

    permissions = this._permissions.asReadonly();
    features = this._features.asReadonly()
    
    constructor(private localStorageSvc: LocalStorageService) {
        this.loadPersistedPermissions();
    }

    /**
     * Loads persisted permissions from localStorage/sessionStorage
     */
    private loadPersistedPermissions(): void {
        const persistedPermissions = this.localStorageSvc.getItem('permissions') || 
                                   JSON.parse(sessionStorage.getItem('permissions') || '[]');
        
        if (persistedPermissions && Array.isArray(persistedPermissions)) {
            this._permissions.set(persistedPermissions);
        } else {
            this._permissions.set([]);
        }
        
        this._features.set([]);
    }

    /**
     * Sets permissions and persists them to storage
     * @param permissions Array of permissions to set
     * @param rememberMe Whether to persist in localStorage (true) or sessionStorage (false)
     */
    public setPermissions(permissions: Permission[], rememberMe: boolean = true) {
        this._permissions.set(permissions);
        this.persistPermissions(permissions, rememberMe);
    }

    /**
     * Persists permissions to storage
     * @param permissions Permissions to persist
     * @param rememberMe Whether to use localStorage or sessionStorage
     */
    private persistPermissions(permissions: Permission[], rememberMe: boolean): void {
        if (rememberMe) {
            this.localStorageSvc.setItem('permissions', permissions);
        } else {
            sessionStorage.setItem('permissions', JSON.stringify(permissions));
        }
    }

    /**
     * Clears all permissions from memory and storage
     */
    public clearPermissions(): void {
        this._permissions.set([]);
        this._features.set([]);
        this.localStorageSvc.removeItem('permissions');
        sessionStorage.removeItem('permissions');
    }

    /**
     * Gets the current permissions from storage
     * @returns Array of permissions or empty array if none found
     */
    public getPersistedPermissions(): Permission[] {
        return this.localStorageSvc.getItem('permissions') || 
               JSON.parse(sessionStorage.getItem('permissions') || '[]');
    }

    /**
     * Checks if permissions are available
     * @returns boolean indicating if permissions are loaded
     */
    public hasPermissions(): boolean {
        const permissions = this.permissions();
        return permissions && permissions.length > 0;
    }

    public checkPermission(resourceId: number, actionId: number): boolean{
        const allPermissions = this.permissions();

        /** Check if the isChecked property is true for the resourceId and actionId */
        return allPermissions && allPermissions.find(x => x.resourceId === resourceId && x.actionId === actionId)?.isChecked;
    }
}